<template>
  <div class="daily-calendar-container">
    <a-divider v-show="!compact">{{ $t('inscription.selectionnerJours') }}</a-divider>
    
    <!-- Calendar for day selection -->
    <div :class="calendarContainerClass">
      <div class="calendar-header">
        <a-button @click="previousMonth" icon="left" />
        <h4 class="calendar-title">{{ calendarValue.format('MMMM YYYY') }}</h4>
        <a-button @click="nextMonth" icon="right" />
      </div>
      
      <div class="calendar-grid">
        <!-- Day headers -->
        <div class="calendar-weekdays">
          <div v-for="day in weekDays" :key="day" class="weekday">{{ day }}</div>
        </div>
        
        <!-- Calendar days -->
        <div class="calendar-days">
          <div 
            v-for="day in calendarDays" 
            :key="day.key"
            class="calendar-day"
            :class="{ 
              'other-month': !day.isCurrentMonth,
              'disabled': day.isDisabled,
              'selected': day.isSelected,
              'paid': day.paymentStatus === 'paid',
              'partially-paid': day.paymentStatus === 'partially-paid',
              'unpaid': day.paymentStatus === 'unpaid'
            }"
            @click="toggleDateSelection(day)"
          >
            <span class="day-number">{{ day.date }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Selected dates summary -->
    <div class="selected-dates-summary mt-3" v-if="showSelectedDates">
      <div class="summary-header">
        <h6>{{ $t('inscription.datesSelectionnees') }} ({{ selectedDates.length }})</h6>
        <a-button 
          v-if="selectedDates.length > 0"
          type="link" 
          size="small"
          @click="clearAllDates"
        >
          {{ $t('inscription.toutEffacer') }}
        </a-button>
      </div>
      
      <div class="selected-dates-tags" >
        <a-tag
          v-for="date in selectedDates"
          :key="date"
          closable
          @close="removeSelectedDate(date)"
          color="blue"
          class="date-tag"
        >
          {{ moment(date).format("DD/MM/YYYY") }}
        </a-tag>
        <div v-if="selectedDates.length === 0" class="no-dates-message">
          {{ $t('inscription.aucuneDateSelectionnee') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import store from "store";

export default {
  name: "DailyCalendar",
  props: {
    showSelectedDates: {
      type: Boolean,
      default: true
    },
    selectedDates: {
      type: Array,
      default: () => []
    },
    activityStart: {
      type: Object,
      default: () => moment()
    },
    activityEnd: {
      type: Object,
      default: () => moment()
    },
    // New prop for payment status data
    paymentData: {
      type: Array,
      default: () => []
    },
    // New prop to control calendar cell height
    compact: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      calendarValue: moment()
    };
  },
  computed: {
    ...mapState(["settings"]),
    weekDays() {
      const localeData = moment.localeData();
      const shortNames = localeData.weekdaysShort(); // Sunday-first by default
      const firstDay = localeData.firstDayOfWeek(); // 0=Sunday, 1=Monday, ...
      const ordered = [];
      for (let i = 0; i < 7; i++) {
        const name = shortNames[(i + firstDay) % 7] || '';
        ordered.push(String(name).replace('.', ''));
      }
      return ordered;
    },
    calendarContainerClass() {
      return {
        'calendar-container': true,
        'compact': this.compact
      };
    },
    calendarDays() {
      const days = [];
      const startOfMonth = this.calendarValue.clone().startOf('month');
      const endOfMonth = this.calendarValue.clone().endOf('month');
      const startOfCalendar = startOfMonth.clone().startOf('week');
      const endOfCalendar = endOfMonth.clone().endOf('week');
      
      let current = startOfCalendar.clone();
      while (current.isSameOrBefore(endOfCalendar)) {
        const dayData = {
          key: current.format('YYYY-MM-DD'),
          date: current.date(),
          moment: current.clone(),
          isCurrentMonth: current.isSame(this.calendarValue, 'month'),
          isDisabled: this.isDateDisabled(current),
          isSelected: this.isDateSelected(current),
          paymentStatus: this.getPaymentStatus(current)
        };
        days.push(dayData);
        current.add(1, 'day');
      }
      return days;
    }
  },
  methods: {
    moment,
    previousMonth() {
      this.calendarValue = this.calendarValue.clone().subtract(1, 'month');
    },
    nextMonth() {
      this.calendarValue = this.calendarValue.clone().add(1, 'month');
    },
    toggleDateSelection(day) {
      if (!day.isDisabled && day.isCurrentMonth) {
        const dateString = day.key;
        const index = this.selectedDates.indexOf(dateString);
        
        // Prevent unselecting paid days
        if (day.paymentStatus === 'paid' || day.paymentStatus === 'partially-paid') {
          return;
        }
        
        if (index > -1) {
          // Remove if already selected (only for non-paid days)
          this.$emit('remove-date', dateString);
        } else {
          // Add if not selected
          this.$emit('add-date', dateString);
        }
      }
    },
    isDateDisabled(current) {    
      // Get activity start and end dates, with fallback to school year
      let activityStart = this.activityStart;
      let activityEnd = this.activityEnd;
      // If activityStart is undefined, use school year start
      if (!activityStart) {
        const schoolarYears = this.settings?.schoolarYears || [];
        const activeSchoolarYear = store.get("app.settings.activeSchoolarYear");
        if (schoolarYears.length > 0 && activeSchoolarYear) {
          const schoolarYear = schoolarYears.find(sc => sc._id === activeSchoolarYear);
          if (schoolarYear) {
            activityStart = this.moment(schoolarYear.start);
          }
        }
      }
        
      // If activityEnd is undefined, use school year end
      if (!activityEnd) {
        const schoolarYears = this.settings?.schoolarYears || [];
        const activeSchoolarYear = store.get("app.settings.activeSchoolarYear");
        if (schoolarYears.length > 0 && activeSchoolarYear) {
          const schoolarYear = schoolarYears.find(sc => sc._id === activeSchoolarYear);
          if (schoolarYear) {
            activityEnd = this.moment(schoolarYear.end);
          }
        }
      }
      
      // If we have both start and end dates, check if current date is outside range
      if (activityStart && activityEnd) {
        const isBeforeStart = current.isBefore(activityStart, 'day');
        const isAfterEnd = current.isAfter(activityEnd, 'day');
        
        // Disable if before start or after end
        if (isBeforeStart || isAfterEnd) {
          return true;
        }
      }
      
      return false;
    },

    getPaymentStatus(moment) {
      const dateString = moment.format('YYYY-MM-DD');
      const paymentInfo = this.paymentData.find(item => item.date === dateString);

      // Only mark days that are actually fees; otherwise, no status
      if (!paymentInfo) {
        return null;
      }

      // Fully paid
      if (paymentInfo.paid === true || paymentInfo.isPayed?.fullyPaid === true) {
        return 'paid';
      }

      // Partially paid (remaining > 0)
      const remaining = paymentInfo.remaining || paymentInfo.isPayed?.remaining || 0;
      if (remaining > 0) {
        return 'partially-paid';
      }

      // Exists as a fee but not paid
      return 'unpaid';
    },
    isDateSelected(moment) {
      return this.selectedDates.includes(moment.format("YYYY-MM-DD"));
    },
    clearAllDates() {
      this.$emit('clear-all-dates');
    },
    removeSelectedDate(date) {
      this.$emit('remove-date', date);
    }
  }
};
</script>

<style scoped>
.daily-calendar-container {
  width: 100%;
}

/* Calendar styling */
.calendar-container {
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 16px;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 0 8px;
}

.calendar-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #262626;
}

.calendar-grid {
  width: 100%;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  margin-bottom: 8px;
}

.weekday {
  text-align: center;
  padding: 8px 4px;
  font-weight: 600;
  color: #8c8c8c;
  font-size: 12px;
  text-transform: uppercase;
}

.calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
}

.calendar-day {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 4px;
  position: relative;
  min-height: 40px;
}

/* Compact mode styles */
.calendar-container.compact .calendar-day {
  height: 45px; /* ultra-compact height */
  min-height: 40px;
  /* remove aspect ratio to keep width from shrinking */
  aspect-ratio: auto;
}

.calendar-container.compact .day-number {
  font-size: 12px;
  font-weight: 500;
  line-height: 12px; /* vertically center number in shorter cell */
}

.calendar-container.compact .calendar-header {
  margin-bottom: 8px;
  padding: 0 4px;
}

.calendar-container.compact .calendar-title {
  font-size: 14px;
}

.calendar-container.compact .weekday {
  padding: 4px 1px;
  font-size: 10px;
  font-weight: 600;
}

.calendar-container.compact .calendar-grid {
  gap: 0.5px;
}

.calendar-container.compact .calendar-weekdays {
  margin-bottom: 4px;
}

.calendar-container.compact .calendar-day.partially-paid::after {
  width: 3px;
  height: 3px;
  top: 1px;
  right: 1px;
}

.calendar-day:hover:not(.disabled):not(.other-month) {
  background-color: #f0f0f0;
  transform: scale(1.05);
}

.calendar-day.selected {
  background-color: #1890ff;
  color: white;
  box-shadow: 0 2px 4px rgba(24, 144, 255, 0.3);
}

.calendar-day.selected:hover {
  background-color: #40a9ff;
  transform: scale(1.05);
}

/* If the selected day is unpaid, show unpaid (red) style instead of blue */
.calendar-day.selected.unpaid {
  background-color: #fc3e48 !important;
  color: white !important;
  box-shadow: none;
}

.calendar-container.compact .calendar-day.selected.unpaid {
  background-color: #fc3e48 !important;
  color: white !important;
  box-shadow: none !important;
}

/* Payment status styles */
.calendar-day.paid {
  background-color: #52c41a !important;
  color: white !important;
  cursor: not-allowed;
  opacity: 0.8;
}

.calendar-day.paid:hover {
  background-color: #52c41a !important;
  transform: none;
  cursor: not-allowed;
}

.calendar-day.partially-paid {
  background-color: #faad14 !important;;
  color: white;
  position: relative;
}

.calendar-day.partially-paid:hover {
  background-color: #ffc53d !important;;
  transform: none;
  cursor: not-allowed;
}

.calendar-day.partially-paid::after {
  content: '';
  position: absolute;
  top: 2px;
  right: 2px;
  width: 6px;
  height: 6px;
  background-color: #ff4d4f;
  border-radius: 50%;
}

/* .calendar-day.unpaid - uses default styling */

/* Make unpaid days clearly visible in visualization mode */
.calendar-day.unpaid {
  background-color: #ffccc7; /* light red tint */
  color: #a8071a;
}

.calendar-container.compact .calendar-day.unpaid {
  background-color: #ffccc7;
  color: #a8071a;
}

.calendar-day.disabled {
  color: #d9d9d9;
  cursor: not-allowed;
  background-color: #f5f5f5;
}

.calendar-day.disabled:hover {
  background-color: #f5f5f5;
  transform: none;
}

.calendar-day.other-month {
  color: #d9d9d9;
  cursor: not-allowed;
}

.calendar-day.other-month:hover {
  background-color: transparent;
  transform: none;
}

.day-number {
  font-weight: 500;
  font-size: 14px;
}

/* Selected dates summary */
.selected-dates-summary {
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 16px;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.summary-header h6 {
  margin: 0;
  color: #262626;
  font-weight: 600;
}

.selected-dates-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.date-tag {
  margin: 0;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
}

.no-dates-message {
  color: #8c8c8c;
  font-style: italic;
  font-size: 14px;
  text-align: center;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 4px;
  border: 1px dashed #d9d9d9;
}
</style>
